import React, { useEffect, useRef } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L, { LatLngExpression} from 'leaflet';
import 'leaflet/dist/leaflet.css';
import hamburgGeoJSON from '../../polygons/hamburg.json';
import altonaDistrictJson from '../../polygons/altona_district.json';
import wandsbekDistrictJson from '../../polygons/wandsbek.json';
import hamburgNordDistrictJson from '../../polygons/hamburgNord.json';
import bergedorfDistrictJson from '../../polygons/bergedorf.json';
import hamburgMitteDistrictJson from '../../polygons/hamburgMitte.json';
import harburgMitteDistrictJson from '../../polygons/harburg.json';
import eimsbüttelDistrictJson from '../../polygons/eimsbüttel.json';
import metropolitanJson from '../../polygons/metropolitan.json'
import {getMarkerIconHtml} from "../../utils/getMarkerIconHtml";
import {useAppSelector} from "../../app/hooks";
import {selectCurrentRequest} from "../../store/requestsReducer";
import {getLineIconHtml} from "../../utils/getLineIconHtml";
import {getDistrictIconHtml} from "../../utils/getDistrictIconHtml";

const geoJsonData = {
    Hamburg: {data: hamburgGeoJSON, center: [53.550278, 9.992222]},
    Altona: {data: altonaDistrictJson, center: [53.54695562875996, 9.935406446456911]},
    Wandsbek: {data: wandsbekDistrictJson, center: [53.57130624926546, 10.070986747741701]},
    'Hamburg-Nord': {data: hamburgNordDistrictJson, center: [53.58994865, 9.984484674464136]},
    Bergedorf: {data: bergedorfDistrictJson, center: [53.487131796280906, 10.220353603363037]},
    'Hamburg-Mitte': {data: hamburgMitteDistrictJson, center: [53.554164354974844, 9.98386859893799]},
    Harburg: {data: harburgMitteDistrictJson, center: [53.4606, 9.9795]},
    Eimsbüttel: {data: eimsbüttelDistrictJson, center: [53.57488895, 9.979073683507066]},
    Metropolregion: {data: metropolitanJson, center: [53.55062078677074, 9.98412609100342]}
};

type GeometryType = 'Point' | 'LineString' | 'Polygon' | 'District';

type GeometryData = {
    type: GeometryType | string;
    coordinates: any;
    title?: string;
    description?: string;
    districtKey?: string;
    icon_type?: {
        name: string;
    };
    id?: string | number;
};

type Props = {
    geometry: GeometryData;
};

const GeometryRenderer = ({ geometry }: Props) => {
    const map = useMap();
    const layerGroupRef = useRef<L.LayerGroup>(L.layerGroup());
    const requestFields = useAppSelector(selectCurrentRequest)

    useEffect(() => {
        const layerGroup = layerGroupRef.current;
        layerGroup.clearLayers();

        let center: LatLngExpression = [0, 0];
        layerGroup.addTo(map);
        if (geometry.type === 'Point') {
            const coords: LatLngExpression = geometry.coordinates;
            center = coords;
            const iconType = requestFields?.icon_type.name || '';
            const iconHtml = getMarkerIconHtml(iconType);

            const pointIcon = L.divIcon({
                html: iconHtml,
                className:
                    iconType === 'Gute Sache' || iconType === 'Problem'
                        ? 'markerIcon_sm'
                        : 'markerIcon_md',
                iconSize: iconType === 'Gute Sache' || iconType === 'Problem' ? [40, 40] : [44, 44],
                iconAnchor: [20, 40],
                popupAnchor: [2, 0],
            });
            const marker = L.marker(coords, { icon: pointIcon });
            marker.addTo(layerGroup);
        }
        if (geometry.type === 'LineString' && Array.isArray(geometry.coordinates)) {
            const coords: LatLngExpression[] = geometry.coordinates;
            center = coords[0];
            const polyline = L.polyline(coords, { color: 'blue' });
            polyline.addTo(layerGroup);

            const iconType = requestFields?.icon_type.name || '';
            const iconHtml = getLineIconHtml(iconType, 'blue');

            const markerPosition = coords.length > 5 ? coords[4] || coords[3] : coords[1] || coords[0];

            const lineIcon = L.divIcon({
                html: iconHtml,
                className: iconType === 'Gute Sache' || iconType === 'Problem' ? 'markerIcon_sm' : 'markerIcon_md',
                iconSize: iconType === 'Gute Sache' || iconType === 'Problem' ? [40, 40] : [44, 44],
                iconAnchor: [20, 40],
                popupAnchor: [0, 0],
            });

            L.marker(markerPosition, { icon: lineIcon }).addTo(layerGroup);
        }

        if (geometry.type === 'Polygon' && Array.isArray(geometry.coordinates)) {
            const coords: LatLngExpression[] = geometry.coordinates;
            const polygon = L.polygon(coords, { color: 'blue' });
            polygon.addTo(layerGroup);
            center = polygon.getCenter();

            const iconType = requestFields?.icon_type.name || '';
            const iconHtml = getMarkerIconHtml(iconType);

            const polygonIcon = L.divIcon({
                html: iconHtml,
                className: iconType === 'Gute Sache' || iconType === 'Problem' ? 'markerIcon_sm' : 'markerIcon_md',
                iconSize: iconType === 'Gute Sache' || iconType === 'Problem' ? [40, 40] : [44, 44],
                iconAnchor: [20, 40],
                popupAnchor: [0, 0],
            });

            L.marker([center.lat, center.lng], { icon: polygonIcon }).addTo(layerGroup);
        }
        // @ts-ignore
        if (geometry.type === 'District' && geometry.districtKey && geoJsonData[geometry.districtKey]) {
            // @ts-ignore
            const { data } = geoJsonData[geometry.districtKey];
            const districtLayer = L.geoJSON(data, {
                style: {
                    color: '#052a09', // green border
                    weight: 2,
                    fillOpacity: 0.1,
                },
                interactive: false,
            }).addTo(layerGroup);

            const bounds = districtLayer.getBounds();
            const center = bounds.getCenter(); // <-- We'll use this for the icon marker

            // Optional: show a center marker based on icon type
            const iconType = requestFields?.icon_type.name || '';
            const iconHtml = getDistrictIconHtml(iconType); // Customize color as needed

            const districtIcon = L.divIcon({
                html: iconHtml,
                className: iconType === 'Gute Sache' || iconType === 'Problem' ? 'markerIcon_sm' : 'markerIcon_md',
                iconSize: iconType === 'Gute Sache' || iconType === 'Problem' ? [40, 40] : [44, 44],
                iconAnchor: [20, 40],
                popupAnchor: [0, 0],
            });

            L.marker([center.lat, center.lng], { icon: districtIcon }).addTo(layerGroup);

            map.fitBounds(bounds);
            return;
        }

        layerGroup.addTo(map);
        map.setView(center, 15);
    }, [geometry, map, requestFields?.icon_type.name]);

    return null;
};

const MapForRequestGeometry = ({ geometry }: Props) => {
    const defaultCenter: LatLngExpression =
        geometry.type === 'Point'
            ? geometry.coordinates
            : Array.isArray(geometry.coordinates)
                ? geometry.coordinates[0]
                : [0, 0];

    return (
        <MapContainer
            center={defaultCenter}
            zoom={13}
            style={{ height: '275px', width: '100%', borderRadius: '12px' }}
        >
            <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='© <a href="https://www.openstreetmap.org/">OpenStreetMap</a> contributors'
            />
            <GeometryRenderer geometry={geometry} />
        </MapContainer>
    );
};

export default MapForRequestGeometry;


